import { Nav, Button } from 'src/components/ui';
import GroupedTabItem from './GroupedTabItem';
import MoveToTabButton, { MoveToTabButtonDef } from './MoveToTabButton';
import { StyledFooterButtonProps } from './types';
import { useMemo } from 'react';
import { Tab } from '../useTabGroupTree';
import { useJnx } from '../../../../util/jnx';
import { isCallable } from '../../../../util/types';

function DefaultFooterButton({ style, btnDef, rootFormData, path, bindings, functionBinds, size }: StyledFooterButtonProps) {
  const { 'ui:onClick': onClickExpr } = btnDef;

  const onClick = useJnx(
    onClickExpr,
    {
      fn: async ([e], onClickJnx) => {
        e.preventDefault();

        if (onClickJnx) {
          await onClickJnx.evalAsync(rootFormData, path, bindings);
        }
      },
      functionBinds,
    },
    [path, rootFormData, bindings, functionBinds]
  );

  return (
    <Button
      onClick={e => {
        if (isCallable(onClick)) onClick(e);
      }}
      size={size}
      style={{ height: 'fit-content', ...style }}
      color={btnDef.color || 'secondary'}
    >
      {btnDef.label}
    </Button>
  );
}

function TabGroupFooterButton({ canBeAsTabPanel, selectedTab, setSelectedTab, schema, rootFormData, bindings }: StyledFooterButtonProps) {
  const depth = 1;
  const ancestorTab = useMemo(() => {
    let tab: Tab | null = selectedTab;
    while (tab && tab.depth > depth) {
      tab = tab.parent;
    }
    return tab;
  }, [selectedTab]);

  const isVisible = useMemo(() => canBeAsTabPanel && (ancestorTab?.items?.length ?? 0) > 1, [canBeAsTabPanel, ancestorTab]);

  if (!isVisible) return null;

  return (
    <Nav tabs className="form-grouped-tabs small btn-small" style={{ height: 'fit-content' }}>
      <GroupedTabItem
        tab={ancestorTab}
        index={0}
        selection={selectedTab}
        onSelect={setSelectedTab}
        schema={schema}
        rootFormData={rootFormData}
        bindings={bindings}
        toggleClassName="footer-btn-grouped-tab-item"
      />
    </Nav>
  );
}

function NextTabFooterButton({
  btnDef,
  canBeAsTabPanel,
  selectedTab,
  setSelectedTab,
  schema,
  tabRoot,
  size,
}: StyledFooterButtonProps<MoveToTabButtonDef>) {
  if (!canBeAsTabPanel) return null;
  return <MoveToTabButton btnDef={btnDef} tabRoot={tabRoot} selectedTab={selectedTab} schema={schema} setSelectedTab={setSelectedTab} size={size} />;
}

const BUTTON_TYPES: Record<string, React.FunctionComponent<StyledFooterButtonProps<any>>> = {
  default: DefaultFooterButton,
  tabGroup: TabGroupFooterButton,
  nextTab: NextTabFooterButton,
};

export default BUTTON_TYPES;
